import { z } from 'zod'
import { translations, type Lang } from '@/lib/i18n'

const ALLOWED_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
])
const MAX_BYTES = 10 * 1024 * 1024

/**
 * Build a Zod schema for the contact form. Parameterised by language so
 * react-hook-form can show localised error messages without an extra
 * translation pass. The shape mirrors api/app/schemas.py::LeadCreate.
 */
export function buildContactSchema(lang: Lang = 'en') {
  const e = translations[lang].contact.errors

  return z.object({
    name: z
      .string({ message: e.nameRequired })
      .trim()
      .min(1, e.nameRequired)
      .max(100, e.nameTooLong),
    company: z
      .string({ message: e.companyRequired })
      .trim()
      .min(1, e.companyRequired)
      .max(200, e.companyTooLong),
    email: z
      .string({ message: e.emailRequired })
      .trim()
      .min(1, e.emailRequired)
      .email(e.emailInvalid)
      .max(254, e.emailInvalid),
    phone: z.string().trim().max(40, e.phoneTooLong).optional().or(z.literal('')),
    country: z.string().trim().max(80, e.countryTooLong).optional().or(z.literal('')),
    message: z.string().trim().max(5000, e.messageTooLong).optional().or(z.literal('')),
    // Files are validated client-side before submit; we keep this lax here
    // so RHF's resolver doesn't fight FileList.
    attachment: z
      .any()
      .optional()
      .refine(
        (f) => !f || (f instanceof File && f.size <= MAX_BYTES),
        { message: e.attachmentTooLarge },
      )
      .refine(
        (f) => !f || (f instanceof File && ALLOWED_TYPES.has(f.type)),
        { message: e.attachmentTypeInvalid },
      ),
    // Honeypot. A real visitor never sees or fills this field (it's rendered
    // off-screen with tabIndex=-1 and aria-hidden). Bots scrape the DOM and
    // fill every input → backend drops those silently. We forward it as-is
    // and never validate length, so bots can't tune around a max.
    website: z.string().optional(),
  })
}

export const contactClientSchema = buildContactSchema('en')

export type ContactFormData = z.infer<ReturnType<typeof buildContactSchema>>
